import React, { useContext, useEffect, useRef, useState } from "react";

import { RemixBuilderContext } from "./RemixBuilderContext";
import TemplatesModal from "../../templates/TemplatesModal";
import useRemix from "./useRemix";

const RemixBuilderSettingsLayouts = () => {
  const { Remix } = useRemix({ builder: true });
  const [contextData, builder, setContextData] = useContext(RemixBuilderContext);
  const sectionsContainerRefs = useRef({});
  const remixButtonRefs = useRef({});
  const [remixTonePopoverSectionId, setRemixTonePopoverSectionId] = useState(null);

  const [remixTemplatesModalOpen, setRemixTemplatesModalOpen] = useState(false);
  const [remixTemplatesModalSection, setRemixTemplatesModalSection] = useState(null);
  const [remixTemplatesModalInitialFilters, setRemixTemplatesModalInitialFilters] = useState({});
  const canAddFunnelStep = builder.canAddFunnelStep();
  const websiteAiEnabled =
    contextData.website?.data?.["ai-enabled"] !== "false";

  const allMatchedSections = contextData.matched_sections || [];
  const sectionStepPositions = Array.from(
    new Set(allMatchedSections.map((section) => section.step_position || 1))
  );
  const namedStepPositions = Object.keys(contextData.funnel_step_names || {})
    .map((stepPosition) => parseInt(stepPosition, 10))
    .filter((stepPosition) => Number.isFinite(stepPosition) && stepPosition > 0);
  const activeStep =
    contextData.active_funnel_step ||
    sectionStepPositions[0] ||
    namedStepPositions[0] ||
    1;
  const stepPositions = Array.from(
    new Set([...sectionStepPositions, ...namedStepPositions, activeStep])
  ).sort((a, b) => a - b);
  const sectionsForStep = (stepPosition) =>
    allMatchedSections
      .filter((section) => (section.step_position || 1) === stepPosition)
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0));

  useEffect(() => {
    const container = sectionsContainerRefs.current[activeStep];
    if (!container) return;
    const sortableInstance = Remix.sortableStart(
      container,
      "matched_sections",
      activeStep
    );

    return () => {
      if (sortableInstance) {
        sortableInstance.destroy();
      }
    };
  }, [activeStep, contextData.matched_sections?.length]);

  const closeRemixTonePopover = () => {
    if (remixTonePopoverSectionId) {
      const activeButton = remixButtonRefs.current[remixTonePopoverSectionId];
      if (activeButton) {
        const $button = $(activeButton);
        const hasPopoverInstance =
          !!$button.data("bs.popover") || !!$button.data("popover");
        if (hasPopoverInstance) {
          $button.popover("hide");
          $button.popover("destroy");
        }
      }
    }
    setRemixTonePopoverSectionId(null);
  };

  const escapeHtml = (value) => {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
  };

  const buildPopoverContent = (sectionId) => {
    const section = contextData.matched_sections.find((s) => s.id === sectionId);
    const value = section?.remix_tone || "";
    return `
      <div style="width: 340px; background: #fff; border-radius: 14px; padding: 10px;">
        <div style="font-size: 12px; line-height: 16px; color: #003FFF; font-weight: 600; margin-bottom: 6px;">
          Remix AI instructions
        </div>
        <textarea
          class="remix-ai-popover-textarea"
          data-section-id="${sectionId}"
          rows="4"
          style="width: 100%; resize: vertical; border: 1px solid #E2E8F0; border-radius: 10px; padding: 10px; font-size: 13px; line-height: 18px; color: #111827;"
          placeholder="Describe tone, messaging, and intent for this section..."
        >${escapeHtml(value)}</textarea>
      </div>
    `;
  };

  const openRemixTonePopover = (buttonEl, sectionId) => {
    const content = buildPopoverContent(sectionId);
    const $button = $(buttonEl);
    const hasPopoverInstance =
      !!$button.data("bs.popover") || !!$button.data("popover");
    if (hasPopoverInstance) {
      $button.popover("hide");
      $button.popover("destroy");
    }
    $button.popover({
      html: true,
      sanitize: false,
      trigger: "manual",
      placement: "left",
      container: "body",
      template:
        '<div class="popover remix-ai-popover" role="tooltip" style="z-index: 10050 !important; border:none; box-shadow: 0 20px 35px rgba(15, 23, 42, 0.18) !important; background: transparent; margin-left: -80px;"><div class="popover-content" style="padding:0; background: transparent;"></div></div>',
      content,
    });
    $button.popover("show");
    setRemixTonePopoverSectionId(sectionId);
  };

  useEffect(() => {
    if (!remixTonePopoverSectionId) return;
    const handleDocumentClick = (event) => {
      const popover = document.querySelector(".remix-ai-popover");
      if (popover?.contains(event.target)) return;
      const activeButton = remixButtonRefs.current[remixTonePopoverSectionId];
      if (activeButton?.contains(event.target)) return;
      closeRemixTonePopover();
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeRemixTonePopover();
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [remixTonePopoverSectionId]);

  useEffect(() => {
    const handleInput = (event) => {
      const target = event.target;
      if (!target.classList.contains("remix-ai-popover-textarea")) return;

      const sectionId = target.dataset.sectionId;
      if (!sectionId) return;

      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: prevContextData.matched_sections.map((section) =>
          String(section.id) === String(sectionId)
            ? { ...section, remix_tone: target.value }
            : section
        ),
      }));
    };

    document.addEventListener("input", handleInput, true);
    return () => document.removeEventListener("input", handleInput, true);
  }, []);

  return (
    <React.Fragment>
      <div className="remix-editor-settings-layouts flex flex-column overflow-scroll grow-1 min-w-0">
        <h2 className="text-[18px] font-semibold mt-15">
          Manage Funnel Steps
        </h2>
        <p className="text-xl text-gray-400">
          Add funnel steps. Manage and position content.
        </p>
        <div className="mt-5 flex flex-col gap-4">
          {stepPositions.map((stepPosition) => {
            const isActive = stepPosition === activeStep;
            const stepSections = sectionsForStep(stepPosition);

            return (
              <div
                key={`content-step-panel-${stepPosition}`}
                className={`relative border border-solid border-slate-200 rounded-xl p-[15px] ${
                  isActive ? "" : "cursor-pointer"
                }`}
                onClick={() => {
                  if (!isActive) builder.setActiveFunnelStep(stepPosition);
                }}
              >
                {stepPositions.length > 1 && (
                  <button
                    type="button"
                    className="absolute top-3 right-3 w-7 h-7 rounded-full border border-solid border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 bg-white flex items-center justify-center p-[5px]"
                    onClick={(e) => {
                      e.stopPropagation();
                      builder.removeFunnelStep(stepPosition);
                    }}
                    aria-label={`Remove funnel step ${stepPosition}`}
                  >
                    <svg
                      width="8.4"
                      height="8.4"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M1 1L9 9M9 1L1 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
                <div
                  className="flex items-center gap-2 pr-10"
                  onClick={() => builder.setActiveFunnelStep(stepPosition)}
                >
                  <span className="text-xl font-semibold text-black whitespace-nowrap">
                    {`Funnel Step ${stepPosition}`}
                  </span>
                  <span className="text-xl font-semibold text-black">-</span>
                  {isActive ? (
                    <input
                      type="text"
                      value={contextData.funnel_step_names?.[stepPosition] || ""}
                      placeholder="Name this step"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        builder.updateFunnelStepName(stepPosition, e.target.value)
                      }
                      className="min-w-0 flex-1 p-2 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0"
                    />
                  ) : (
                    <span className="text-xl text-gray-600 whitespace-nowrap text-ellipsis overflow-hidden">
                      {contextData.funnel_step_names?.[stepPosition] || "Name this step"}
                    </span>
                  )}
                </div>

                <div
                  ref={(el) => {
                    sectionsContainerRefs.current[stepPosition] = el;
                  }}
                  id={`remix-matched-sections-templates-${stepPosition}`}
                  className="flex flex-wrap gap-3 overflow-scroll mt-3"
                >
                  {stepSections.map((section) => (
                    <React.Fragment key={`section-selector-${section.id}`}>
                      <div
                        className={`matched-section-template flex gap-2 section-selector-box text-2xl items-center justify-between w-full py-4 ${
                          isActive ? "cursor-move" : ""
                        }`}
                        data-section-id={section.id}
                        data-position={section.position}
                        style={{ borderBottom: "1px inset #f2f2f2" }}
                      >
                      <div className="flex gap-2 items-start min-w-0 flex-1">
                        {isActive ? (
                          <input
                            onChange={() =>
                              builder.toggleSelectSection(section, "matched_sections")
                            }
                            checked={
                              contextData.matched_sections.find(
                                (matchedSection) => matchedSection.id === section.id
                              )?.selected
                            }
                            type="checkbox"
                            className="w-[20px] h-[20px] border-2 border-solid border-slate-300 rounded-[50%] cursor-pointer ml-3"
                          />
                        ) : (
                          <span className="text-xl ml-3">•</span>
                        )}
                        <div className="whitespace-nowrap text-ellipsis overflow-hidden text-xl select-none mt-1 min-w-0 flex-1 pr-2">
                          {`${section?.template?.name}`}
                        </div>
                      </div>

                        {isActive && (
                          <div className="flex gap-2 items-center flex-shrink-0">
                          {websiteAiEnabled && (
                            <span
                              role="button"
                              tabIndex={0}
                              className="bg-white py-[2px] px-[10px] border-solid border-[1px] border-[#6B8CFF] rounded-xl flex gap-1 items-center cursor-pointer text-xl mt-2 text-[#003FFF] hover:text-[#003FFF] hover:border-[#003FFF]"
                              ref={(el) => {
                                remixButtonRefs.current[section.id] = el;
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (remixTonePopoverSectionId === section.id) {
                                  closeRemixTonePopover();
                                  return;
                                }
                                closeRemixTonePopover();
                                openRemixTonePopover(e.currentTarget, section.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (remixTonePopoverSectionId === section.id) {
                                    closeRemixTonePopover();
                                    return;
                                  }
                                  closeRemixTonePopover();
                                  openRemixTonePopover(e.currentTarget, section.id);
                                }
                              }}
                            >
                              Remix
                            </span>
                          )}
                          <span
                            className="bg-white py-[2px] px-[8px] border-solid border-[1px] border-slate-300 rounded-xl flex gap-1 items-center cursor-pointer text-xl mt-2"
                            onClick={() => {
                              setRemixTemplatesModalSection(section);
                              const transformedTags =
                                Remix.transformSectionTagsForModal(section.tags);
                              setRemixTemplatesModalInitialFilters(transformedTags);
                              setRemixTemplatesModalOpen(true);
                            }}
                          >
                            <svg
                              width="14"
                              height="15"
                              viewBox="0 0 14 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6.59844 1.8999C6.59844 1.54092 6.30742 1.2499 5.94844 1.2499C5.58945 1.2499 5.29844 1.54092 5.29844 1.8999H6.59844ZM5.29844 13.0999C5.29844 13.4589 5.58945 13.7499 5.94844 13.7499C6.30742 13.7499 6.59844 13.4589 6.59844 13.0999H5.29844ZM3.49844 2.5499H10.4984V1.2499H3.49844V2.5499ZM11.9484 3.9999V10.9999H13.2484V3.9999H11.9484ZM10.4984 12.4499H3.49844V13.7499H10.4984V12.4499ZM2.04844 10.9999V3.9999H0.748438V10.9999H2.04844ZM3.49844 12.4499C2.69762 12.4499 2.04844 11.8007 2.04844 10.9999H0.748438C0.748438 12.5187 1.97965 13.7499 3.49844 13.7499V12.4499ZM11.9484 10.9999C11.9484 11.8007 11.2992 12.4499 10.4984 12.4499V13.7499C12.0172 13.7499 13.2484 12.5187 13.2484 10.9999H11.9484ZM10.4984 2.5499C11.2992 2.5499 11.9484 3.19909 11.9484 3.9999H13.2484C13.2484 2.48112 12.0172 1.2499 10.4984 1.2499V2.5499ZM3.49844 1.2499C1.97965 1.2499 0.748438 2.48112 0.748438 3.9999H2.04844C2.04844 3.19909 2.69762 2.5499 3.49844 2.5499V1.2499ZM5.29844 1.8999V13.0999H6.59844V1.8999H5.29844ZM5.94844 8.1499H12.2484V6.8499H5.94844V8.1499Z"
                                fill="black"
                              />
                            </svg>
                            Change
                          </span>
                          <div className="handler cursor-pointer mt-2">
                            <svg
                              width="20"
                              height="21"
                              viewBox="0 0 20 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M7.91797 8C7.22761 8 6.66797 7.44036 6.66797 6.75C6.66797 6.05964 7.22761 5.5 7.91797 5.5C8.60832 5.5 9.16797 6.05964 9.16797 6.75C9.16797 7.44036 8.60832 8 7.91797 8Z" fill="black" />
                              <path d="M7.91797 11.75C7.22761 11.75 6.66797 11.1904 6.66797 10.5C6.66797 9.80964 7.22761 9.25 7.91797 9.25C8.60832 9.25 9.16797 9.80964 9.16797 10.5C9.16797 11.1904 8.60832 11.75 7.91797 11.75Z" fill="black" />
                              <path d="M7.91797 15.5C7.22761 15.5 6.66797 14.9404 6.66797 14.25C6.66797 13.5596 7.22761 13 7.91797 13C8.60832 13 9.16797 13.5596 9.16797 14.25C9.16797 14.9404 8.60832 15.5 7.91797 15.5Z" fill="black" />
                              <path d="M12.0859 8C11.3956 8 10.8359 7.44036 10.8359 6.75C10.8359 6.05964 11.3956 5.5 12.0859 5.5C12.7763 5.5 13.3359 6.05964 13.3359 6.75C13.3359 7.44036 12.7763 8 12.0859 8Z" fill="black" />
                              <path d="M12.0859 11.75C11.3956 11.75 10.8359 11.1904 10.8359 10.5C10.8359 9.80964 11.3956 9.25 12.0859 9.25C12.7763 9.25 13.3359 9.80964 13.3359 10.5C13.3359 11.1904 12.7763 11.75 12.0859 11.75Z" fill="black" />
                              <path d="M12.0859 15.5C11.3956 15.5 10.8359 14.9404 10.8359 14.25C10.8359 13.5596 11.3956 13 12.0859 13C12.7763 13 13.3359 13.5596 13.3359 14.25C13.3359 14.9404 12.7763 15.5 12.0859 15.5Z" fill="black" />
                            </svg>
                          </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {isActive && (
                  <span
                    style={{ display: "block" }}
                    className="cursor-pointer text-center card-new !h-[50px] p-[10px] !pt-[15px] flex items-center justify-center text-gray-700 text-xl mt-6"
                    onClick={() => {
                      setRemixTemplatesModalSection(null);
                      setRemixTemplatesModalInitialFilters({});
                      setRemixTemplatesModalOpen(true);
                    }}
                  >
                    Add Section
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-5">
          <button
            type="button"
            disabled={!canAddFunnelStep}
            onClick={() => {
              if (!canAddFunnelStep) return;
              builder.addFunnelStep();
            }}
            className={`inline-flex items-center px-4 py-2 rounded-full text-xl border border-dashed bg-transparent ${
              canAddFunnelStep
                ? "border-slate-400 text-gray-700"
                : "border-slate-300 text-slate-400 cursor-not-allowed opacity-60"
            }`}
          >
            + Add Funnel Step
          </button>
        </div>
      </div>

      <div className="remix-editor-settings-buttons pt-[30px] pb-[30px] gap-4 flex justify-between">

        {contextData.from_scratch !== true && (
          <div
            onClick={() => builder.goToPreviousStep()}
            className="cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center gap-2 text-xl border border-solid border-slate-200 bg-white"
          >
            Back
          </div>
        )}

        <div
          onClick={() => builder.goToNextStep()}
          className="cursor-pointer rounded-xl w-full p-[10px] border border-solid border-black bg-black flex justify-center items-center text-white gap-2 text-xl"
        >
          Next
        </div>
      </div>

      <TemplatesModal
        website={contextData.website}
        isOpen={remixTemplatesModalOpen}
        onClose={() => {
          setRemixTemplatesModalOpen(false);
          setRemixTemplatesModalSection(null);
          setRemixTemplatesModalInitialFilters({});
        }}
        templates={contextData.section_templates}
        selectedSection={remixTemplatesModalSection}
        initialFilters={remixTemplatesModalInitialFilters}
        onSelectTemplate={(sectionTemplate) => {
          console.log('remixTemplatesModalSection', remixTemplatesModalSection)

          if (remixTemplatesModalSection) {
            builder.changeMatchedSectionTemplate(remixTemplatesModalSection, sectionTemplate, true);
            remixTemplatesModalSection.remixMatched = false;
            remixTemplatesModalSection.currentTemplateIndex = 0;
          } else {
            builder.addNewMatchedSection(sectionTemplate, activeStep);
          }
        }}
      >
        <div className="flex justify-between items-center mt-6 mb-6">
          <h4 className="m-0 text-[18px] font-semibold">Choose a template</h4>
        </div>
      </TemplatesModal>
    </React.Fragment>
  );
};

export default RemixBuilderSettingsLayouts;
